import React, { useEffect, useMemo, useState } from "react"
import { browserHistory } from "react-router"
import SweetAlert from "react-bootstrap-sweetalert"
import ReactTooltip from "react-tooltip"
import Loader from "../../Components/Loader"
import moment from "moment"
import { Site } from "../../types/Site"
import { computeEquitableDistribution, getCampaign, getOccurrence, getTargetsDistribution, getUnfollowedTargets, targetAssignment } from "../../Lib/MarketingCampaign"
import { Campagne, CampagneTargetType, Occurrence, RedistributionEntry, Target, TargetDistribution } from "../../types/MarketingCampaign"
import { User } from "../../types/User"
import Switch from "../../Components/Switch/Switch"
import LibUtilisateurs from "../../Lib/Utilisateurs"
import ModalRepartitionSolde from "./ModalRepartitionSolde"

enum SelectedSellerType
{
    ALL = "all",
    VN = "VN",
    VO = "VO",
}

interface TotalCounter {
    totalTargets: number
    totalRemaining: number
    totalSelected: number
    totalSelectedTargets: number
    totalFollowedWithAssignment: number
    totalLeadsProcessed: number
    totalLeadsTargets: number
    totalUnassignedTargetsAssigned: number
}

type CampagneDistributionProps = {
    params: { id: number }
    location: { query: { dealership_id?: string } }
}

export default function CampagneDistribution(props: CampagneDistributionProps) {

    const [ isLoading, setIsLoading ] = useState(false)
    const [ campagne, setCampagne ] = useState<Campagne | null>(null)
    const [ campagneSites, setCampagneSites ] = useState<Site[]>([])
    const [ userSites, setUserSites ] = useState<Site[]>([])
    const [ dealershipSellers, setDealershipSellers ] = useState<User[]>([])
    const [ sites, setSites ] = useState<Site[]>([])
    const [ campaignOccurrences, setCampaignOccurrences ] = useState<Occurrence[]>([])
    const [ targetsDistribution, setTargetsDistribution ] = useState<TargetDistribution[]>([])
    const [ redistributionEntries, setRedistributionEntries ] = useState<RedistributionEntry[]>([])
    const [ maxLeadNumber, setMaxLeadNumber ] = useState<number>(0)
    const [ selectedSellerType, setSelectedSellerType ] = useState<SelectedSellerType>(SelectedSellerType.ALL)
    const [ dealershipID, setDealershipID ] = useState<number>(Number(props.location.query.dealership_id) || 0)
    const [ totalCounter, setTotalCounter ] = useState<TotalCounter>({
        totalTargets: 0,
        totalRemaining: 0,
        totalSelected: 0,
        totalSelectedTargets: 0,
        totalFollowedWithAssignment: 0,
        totalLeadsProcessed: 0,
        totalLeadsTargets: 0,
        totalUnassignedTargetsAssigned: 0,
    })
    const [ selectedOccurrence, setSelectedOccurrence ] = useState<Occurrence>()
    const [ isStatisticsView, setIsStatisticsView ] = useState(false)
    const [ hasRedistribution, setHasRedistribution ] = useState(false)
    const [ isRedistributing, setIsRedistributing ] = useState(false)
    const [ isSellerModalOpen, setIsSellerModalOpen ] = useState(false)
    const [ alertMessage, setAlertMessage ] = useState<{ type: "error" | "success", text: string } | null>(null)
    const [ isSubmittingAssignment, setIsSubmittingAssignment ] = useState(false)
    // Commerciaux retenus lors de la dernière validation de la modale : sert à les re-cocher si on la
    // rouvre.
    const [ selectedRedistributionSellerIds, setSelectedRedistributionSellerIds ] = useState<number[]>([])

    // Libellé du type d'utilisateur ciblé par la campagne, utilisé dans tous les textes de la page.
    const sellerLabel: string = useMemo(() => (
        campagne && campagne.target_type === CampagneTargetType.ADV ? "Conseiller" : "Vendeur"
    ), [ campagne ])

    const sellerLabelPlural: string = sellerLabel + "s"

    // La ligne "Contact non assignés" (id 0) n'a pas de type de vendeur : on la conserve toujours,
    // sinon elle disparaît du tableau dès qu'un filtre VN/VO est actif (et la case "Attribuer le
    // solde restant" avec elle).
    const targetsDistributionFiltered: TargetDistribution[] = useMemo(() => {
        if (selectedSellerType === "all") {
            return targetsDistribution
        } else {
            return targetsDistribution.filter((td: TargetDistribution) => td.followed_by.id === 0 || td.followed_by.types.includes(selectedSellerType))
        }
    }, [ targetsDistribution, selectedSellerType ])

    const filteredRedistributionEntries: RedistributionEntry[] = useMemo(() => {
        if (selectedSellerType === "all") {
            return redistributionEntries
        } else {
            return redistributionEntries.filter((td: RedistributionEntry) => td.followed_by.types.includes(selectedSellerType))
        }
    }, [ redistributionEntries, selectedSellerType ])

    const redistributedCountByUser: Record<number, number> = useMemo(() => {
        return filteredRedistributionEntries.reduce((acc: Record<number, number>, td: RedistributionEntry) => {
            acc[td.followed_by.id] = (td.target_ids || []).length
            return acc
        }, {})
    }, [ filteredRedistributionEntries ])

    const redistributedTotal: number = useMemo(() => {
        return redistributionEntries.reduce((sum: number, td: RedistributionEntry) => sum + (td.target_ids || []).length, 0)
    }, [ redistributionEntries ])

    // Solde des contacts non assignés à répartir dans la modale : le solde affiché a déjà été
    // réduit de la part provisoirement redistribuée (redistributionEntries) tant que
    // l'attribution n'a pas été validée. On la rajoute pour retomber sur le pool total,
    // sans quoi rouvrir la modale sur une répartition déjà saisie affiche un solde disponible à 0.
    const unassignedSolde: number = useMemo(() => {
        const unassignedRow = targetsDistributionFiltered.find((td: TargetDistribution) => td.followed_by.id === 0)
        return ((unassignedRow && unassignedRow.solde) || 0) + redistributedTotal
    }, [ targetsDistributionFiltered, redistributedTotal ])

    // Commerciaux éligibles à la répartition du solde : tous les vendeurs du site, et pas seulement
    // ceux qui ont déjà une ligne dans la distribution des cibles (un vendeur sans cible actuelle doit
    // pouvoir en recevoir via la répartition). On complète avec les vendeurs du site n'ayant pas encore
    // de ligne par une entrée à zéro ; hors ligne "Contact non assignés" et hors pots communs.
    const eligibleSellers: TargetDistribution[] = useMemo(() => {
        const distributionByUserId = new Map<number, TargetDistribution>(
            targetsDistribution
                .filter((td: TargetDistribution) => td.followed_by.id !== 0 && !td.is_shared_pool)
                .map((td: TargetDistribution): [number, TargetDistribution] => [ td.followed_by.id, td ]),
        )

        const merged: TargetDistribution[] = dealershipSellers.map((utilisateur: User) => {
            const existing = distributionByUserId.get(utilisateur.IDUtilisateur)
            distributionByUserId.delete(utilisateur.IDUtilisateur)
            if (existing) {
                return existing
            }
            const types: string[] = []
            if (utilisateur.EstTypeVN) { types.push("VN") }
            if (utilisateur.EstTypeVO) { types.push("VO") }
            return {
                followed_by: { id: utilisateur.IDUtilisateur, name: utilisateur.Nom, types },
                followed_by_user_id: utilisateur.IDUtilisateur,
                selected_targets: 0,
                solde: 0,
                targets_count: 0,
                followed_with_assignment_count: 0,
                leads_targets_count: 0,
                processed_leads_targets_count: 0,
            }
        })

        // Un commercial de la distribution absent de la liste des vendeurs du site (désactivé
        // entre-temps, etc.) reste éligible pour ne pas lui faire perdre une affectation en cours.
        merged.push(...distributionByUserId.values())

        return merged
            .filter((td: TargetDistribution) => selectedSellerType === "all" || td.followed_by.types.includes(selectedSellerType))
            .sort((a: TargetDistribution, b: TargetDistribution) => (a.followed_by.name || "").localeCompare(b.followed_by.name || ""))
    }, [ dealershipSellers, targetsDistribution, selectedSellerType ])

    // Raison pour laquelle le bouton "Attribuer" est désactivé, affichée en tooltip ; null si
    // l'attribution en cours de saisie est valide.
    const submitDisabledReason: string | null = useMemo(() => {
        const overAssignedSeller = targetsDistributionFiltered.find((td: TargetDistribution) => (
            td.followed_by.id !== 0 && td.selected_targets > (td.targets_count - td.followed_with_assignment_count)
        ))
        if (overAssignedSeller) {
            return `L'affectation de ${overAssignedSeller.followed_by.name} dépasse le nombre de cibles disponibles pour ce ${sellerLabel.toLowerCase()}.`
        }
        if (totalCounter.totalSelectedTargets > totalCounter.totalTargets) {
            return "Le nombre total de cibles sélectionnées dépasse le nombre de cibles disponibles."
        }
        if (!totalCounter.totalSelectedTargets) {
            return "Aucune cible n'a été attribuée."
        }
        return null
    }, [ targetsDistributionFiltered, totalCounter, sellerLabel ])

    useEffect(() => {
        fetchData(props.params.id, dealershipID)
    }, [])

    useEffect(() => {
        if (campagneSites.length > 0 && userSites.length > 0) {
            let idsUsersSites = userSites.map((site: any) => site.IDSite)
            const filteredSites = campagneSites.filter((site: Site) => {
                return idsUsersSites.includes(site.id)
            })
            setSites(filteredSites)
        }
    }, [ campagneSites, userSites ])

    const fetchData = async (campaignID: number, dealershipId: number) => {
        setIsLoading(true)

        await LibUtilisateurs.getSites(localStorage.user_IDUtilisateur).then(
            (sites) => setUserSites(sites),
        )

        const campaign = await getCampaign(campaignID)
        setCampagne(campaign)
        setCampagneSites(campaign.dealerships)
        setCampaignOccurrences(campaign.occurrences)

        const occurrenceId = campaign.occurrences[0].id
        const [ occurrence, targetDistributions, sellers ] = await Promise.all([
            getOccurrence(campaignID, occurrenceId, dealershipId),
            getTargetsDistribution(campaignID, occurrenceId, dealershipId),
            LibUtilisateurs.getUtilisateursManages(localStorage.user_IDUtilisateur, dealershipId),
        ])

        setDealershipSellers((sellers || []).filter((utilisateur: User) => (
            campaign.target_type === CampagneTargetType.ADV ? utilisateur.EstTypeConseiller : utilisateur.TypeProfilLead === "Vendeur"
        )))
        setSelectedOccurrence(occurrence)
        setHasRedistribution(false)
        setSelectedRedistributionSellerIds([])
        setRedistributionEntries([])
        calculateSolde(targetDistributions.filter((td: TargetDistribution) => !td.is_shared_pool), maxLeadNumber, occurrence)
        setIsLoading(false)
    }

    const onChangeDealership = (dealershipID: number) => {
        setDealershipID(dealershipID)
        browserHistory.replace(`/campagnes-marketing/${props.params.id}?dealership_id=${dealershipID}`) //Pour synchro le dealership_id dans l'url
        fetchData(props.params.id, dealershipID)
    }

    useEffect(() => {
        calculateTotalSelected()
    }, [ targetsDistributionFiltered, redistributedTotal ])

    const renderCardRow = (label: string, value: string) => {
        return (
            <div className="cd-card-row">
                <div className="cd-card-label">
                    <p>{label}</p>
                </div>
                <div className="cd-card-value">
                    <p>{value}</p>
                </div>
            </div>
        )
    }

    const calculateTotalSelected = () => {
        let totalTargets: number = 0
        let totalRemaining: number = 0
        let totalSelected: number = 0
        let totalSelectedTargets: number = 0
        let totalFollowedWithAssignment: number = 0
        let totalLeadsProcessed: number = 0
        let totalLeadsTargets: number = 0
        let totalUnassignedTargetsAssigned: number = 0

        targetsDistributionFiltered.forEach((td: TargetDistribution): void => {
            totalTargets += td.targets_count
            totalRemaining += td.solde
            if (td.followed_by && td.followed_by.id !== 0 && td.followed_by.id !== null) {
                totalSelected += td.selected_targets || 0
                totalUnassignedTargetsAssigned += Math.max(0, (td.assigned_targets_count || 0) - (td.followed_and_assigned_targets_count || 0))
            }
            totalFollowedWithAssignment += td.followed_with_assignment_count
            totalLeadsProcessed += td.processed_leads_targets_count
            totalLeadsTargets += td.leads_targets_count

        })

        // Le solde redistribué vit dans un état à part (redistributionEntries) : sans ça
        // il ne serait compté nulle part et le total d'affectation ne bougerait pas. On utilise le
        // total non filtré, le même que celui retiré du solde des non assignés, pour que les deux
        // colonnes se réconcilient.
        totalSelectedTargets = totalSelected + redistributedTotal

        setTotalCounter({
            totalTargets,
            totalRemaining,
            totalSelected,
            totalSelectedTargets,
            totalFollowedWithAssignment,
            totalLeadsProcessed,
            totalLeadsTargets,
            totalUnassignedTargetsAssigned,
        })
    }

    // alreadyRedistributed : part du solde des non assignés déjà attribuée aux commerciaux via
    // "Attribuer le solde restant" — à retirer du solde affiché sur cette ligne.
    const calculateSolde = (
        targetsDistributions: TargetDistribution[], maxLead: number, occurrence: Occurrence, isChangMaxLead: boolean = false,
        alreadyRedistributed: number = 0,
    ) => {
        const newSolde = maxLead * ((occurrence && occurrence.business_days_count) || 0)
        const updatedTargetsDistribution = targetsDistributions.map((td: TargetDistribution) => {
            const remainingTargets = td.targets_count - td.followed_with_assignment_count
            // Tant qu'aucun "nombre maximum / jour" n'est renseigné, il n'y a pas de plafond à
            // appliquer : sans ce garde-fou toute saisie manuelle était ramenée à 0.
            const selected_targets = (newSolde <= 0 && !isChangMaxLead)
                ? td.selected_targets
                : (remainingTargets <= newSolde ? (isChangMaxLead ? remainingTargets : td.selected_targets) : newSolde)
            if (td.followed_by.id === 0) {
                return {
                    ...td,
                    selected_targets: 0,
                    solde: (td.targets_count - td.followed_with_assignment_count) - alreadyRedistributed,
                }
            } else {

                return {
                    ...td,
                    selected_targets,
                    solde: (td.targets_count - td.followed_with_assignment_count) - (selected_targets || 0),
                }
            }
        })
        setTargetsDistribution(updatedTargetsDistribution)
        setMaxLeadNumber(maxLead)
    }

    const changeTargetDistribution = (data: TargetDistribution, target: number) => {
        const updatedTargetsDistribution = targetsDistribution.map((item: TargetDistribution) => {
            return item.followed_by.id === data.followed_by.id
                ? {
                    ...item,
                    selected_targets: target,
                }
                : item
        })
        calculateSolde(updatedTargetsDistribution, maxLeadNumber, selectedOccurrence, false, redistributedTotal)
    }

    const resetRemainingDistribution = () => {
        const updatedTargetsDistribution = targetsDistribution.map((item: TargetDistribution) => {
            if (item.followed_by.id === 0) {
                return {
                    ...item,
                    selected_targets: 0,
                    solde: item.targets_count - item.followed_with_assignment_count,
                }
            }
            return item
        })
        setTargetsDistribution(updatedTargetsDistribution)
        setRedistributionEntries([])
        setSelectedRedistributionSellerIds([])
        setHasRedistribution(false)
    }

    // Répartit le solde des contacts non assignés sur les seuls commerciaux choisis dans la modale.
    // manualAssignments : nombre exact voulu pour certains commerciaux (saisi en nombre ou en
    // pourcentage dans la modale, déjà converti en nombre absolu) ; les commerciaux sélectionnés
    // sans entrée dans cette map reçoivent leur part via la répartition équitable habituelle.
    const distributeRemainingTargets = async (sellerIds: number[], manualAssignments: Record<number, number> = {}) => {
        setIsSellerModalOpen(false)

        const targetSellers = eligibleSellers.filter((td: TargetDistribution) => sellerIds.indexOf(td.followed_by.id) !== -1)

        if (targetSellers.length === 0) {
            resetRemainingDistribution()
            return
        }

        setIsRedistributing(true)

        let targets
        try {
            targets = await getUnfollowedTargets(campagne.id, dealershipID, selectedOccurrence.id)
        } catch (error) {
            console.error("Erreur lors de la récupération des cibles non assignées :", error)
            setAlertMessage({ type: "error", text: "Les cibles à répartir n'ont pas pu être récupérées." })
            setIsRedistributing(false)
            return
        }

        // getUnfollowedTargets ne filtre que sur followed_by_user_id=0, qui n'est jamais mis à jour
        // par notre attribution (elle ne pose que seller_user_id) : sans ce filtre, une cible déjà
        // affectée lors d'une précédente répartition validée réapparaîtrait comme disponible ici.
        const targetsToRedistributeID: number[] = targets
            .filter((target: Target) => !target.seller_user_id)
            .map((target: Target) => target.id)

        // On répartit le stock réel de cibles remonté par l'API, et non un compteur de la ligne
        // "non assignés" : au cas où il y ait eu des changements en base entre-temps.
        const totalTarget = targetsToRedistributeID.length

        // Les commerciaux avec une saisie manuelle sont servis en premier, dans l'ordre de la liste
        // (triée par nom, donc stable) : chacun reçoit exactement sa saisie, plafonnée par ce qu'il
        // reste réellement en stock si la saisie totale dépasse le stock réel remonté par l'API.
        const manualTakes: number[] = targetSellers.map((seller: TargetDistribution) => {
            const manualCount = manualAssignments[seller.followed_by.id]
            return manualCount !== undefined ? Math.max(0, manualCount) : 0
        })
        const autoIndexes: number[] = targetSellers
            .map((_seller: TargetDistribution, index: number) => index)
            .filter((index: number) => manualAssignments[targetSellers[index].followed_by.id] === undefined)

        const takes: number[] = targetSellers.map(() => 0)
        let remainingPool = totalTarget
        targetSellers.forEach((_seller: TargetDistribution, index: number) => {
            takes[index] = Math.min(manualTakes[index], remainingPool)
            remainingPool -= takes[index]
        })

        // Équité "au final" et non sur la seule part distribuée, sur le pool restant après les
        // saisies manuelles : même algorithme que l'aperçu de la modale (voir computeEquitableDistribution).
        const autoTakes: number[] = computeEquitableDistribution(autoIndexes.map((index: number) => targetSellers[index]), remainingPool)
        autoIndexes.forEach((sellerIndex: number, autoIndex: number) => {
            takes[sellerIndex] = autoTakes[autoIndex]
        })

        let offset = 0
        const updatedTargetsDistributionUser = targetSellers
            .map((seller: TargetDistribution, index: number) => {
                const targetIds = targetsToRedistributeID.slice(offset, offset + takes[index])
                offset += takes[index]
                return {
                    followed_by: { id: seller.followed_by.id, name: seller.followed_by.name, types: seller.followed_by.types },
                    target_ids: targetIds,
                }
            })
            .filter((entry) => entry.target_ids.length > 0)

        // Une saisie manuelle peut volontairement ne pas consommer tout le stock (ex : 6 affectés
        // sur 17 disponibles) : le solde restant doit refléter ce qui a vraiment été distribué, pas
        // le stock total récupéré côté API, sans quoi le reliquat non affecté disparaîtrait.
        const actuallyRedistributed = takes.reduce((sum: number, take: number) => sum + take, 0)

        const targetsDistributionToSend: TargetDistribution[] = targetsDistribution.map((item: TargetDistribution) => {
            if (item.followed_by.id === 0) {
                return {
                    ...item,
                    selected_targets: 0,
                    solde: (item.targets_count - item.followed_with_assignment_count) - actuallyRedistributed,
                }
            }
            return item
        })

        setTargetsDistribution(targetsDistributionToSend)
        setRedistributionEntries(updatedTargetsDistributionUser)
        setSelectedRedistributionSellerIds(targetSellers.map((seller: TargetDistribution) => seller.followed_by.id))
        setHasRedistribution(true)
        setIsRedistributing(false)
    }

    // Changer le type de vendeur change la population disponible : la sélection faite dans la modale
    // n'a plus forcément de sens, on repart de zéro plutôt que de redistribuer sans action explicite.
    useEffect(() => {
        if (hasRedistribution) {
            resetRemainingDistribution()
        }
    }, [ selectedSellerType ])

    // Recharge l'occurrence et sa distribution depuis l'API et remet à zéro les intentions locales
    // (affectations saisies, répartition du solde) : après un changement d'occurrence comme après une
    // attribution, seules les données serveur font foi.
    const reloadOccurrenceData = async (occurrenceId: number) => {
        const [ newOccurrence, targetDistributions ] = await Promise.all([
            getOccurrence(props.params.id, occurrenceId, dealershipID),
            getTargetsDistribution(props.params.id, occurrenceId, dealershipID),
        ])

        setSelectedOccurrence(newOccurrence)
        setHasRedistribution(false)
        setSelectedRedistributionSellerIds([])
        setRedistributionEntries([])
        // calculateSolde pose déjà targetsDistribution : un setTargetsDistribution avec les données
        // brutes juste après écrasait le calcul (dernier write gagnant sur le même batch).
        calculateSolde(targetDistributions.filter((td: TargetDistribution) => !td.is_shared_pool), maxLeadNumber, newOccurrence)
    }

    const changeOccurrenceSelected = async (occurrenceId: number) => {

        setIsLoading(true)

        try {
            await reloadOccurrenceData(occurrenceId)
        } catch (error) {
            console.error("Erreur lors du chargement de l'occurrence :", error)
            setAlertMessage({ type: "error", text: "L'occurrence n'a pas pu être chargée." })
        } finally {
            setIsLoading(false)
        }
    }

    const changeMaxLeadNumber = (max: number) => {
        calculateSolde(targetsDistribution, max, selectedOccurrence, true, redistributedTotal)
    }

    const submitTargetsAssignment = async () => {
        setIsSubmittingAssignment(true)

        try {
            await targetAssignment(props.params.id, selectedOccurrence.id, targetsDistributionFiltered, filteredRedistributionEntries, dealershipID)
        } catch (error) {
            console.error("Erreur lors de l'attribution des cibles :", error)
            setAlertMessage({ type: "error", text: "Une erreur est survenue lors de l'attribution des cibles." })
            setIsSubmittingAssignment(false)
            return
        }

        // On relit l'API plutôt que de recharger la page : "Relations affectées" et les soldes
        // viennent du serveur et ne bougent qu'après l'attribution.
        try {
            await reloadOccurrenceData(selectedOccurrence.id)
            setAlertMessage({ type: "success", text: "Les cibles ont été attribuées avec succès." })
        } catch (error) {
            // L'attribution est passée : on le dit explicitement pour éviter une seconde soumission.
            console.error("Erreur lors du rafraîchissement de la distribution :", error)
            setAlertMessage({
                type: "error",
                text: "L'attribution a bien été enregistrée, mais l'écran n'a pas pu être rafraîchi. Rechargez la page pour voir les données à jour.",
            })
        } finally {
            setIsSubmittingAssignment(false)
        }
    }

    if (isLoading) {
        return (
            <Loader/>
        )
    }
    if (!campagne) {
        return (
            <div className="campagne-distribution">
                <header className="cd-header">
                    <h1>Aucune campagne trouvée</h1>
                </header>
            </div>
        )
    }
    return (
        <div className="campagne-distribution">
            {isSubmittingAssignment ? (
                <SweetAlert title="" showConfirm={false} onConfirm={() => {}}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <i className="fa fa-spinner fa-spin fa-3x" aria-hidden="true"/>
                        <span style={{ marginTop: 15 }}>Attribution des cibles en cours…</span>
                    </div>
                </SweetAlert>
            ) : null}
            {alertMessage ? (
                alertMessage.type === "success" ? (
                    <SweetAlert success title="" onConfirm={() => setAlertMessage(null)} confirmBtnBsStyle="default">
                        {alertMessage.text}
                    </SweetAlert>
                ) : (
                    <SweetAlert error title="" onConfirm={() => setAlertMessage(null)} confirmBtnBsStyle="default">
                        {alertMessage.text}
                    </SweetAlert>
                )
            ) : null}

            {/* Header */}
            <header className="cd-header">
                <div>
                    <h1>{campagne.name || " - "}</h1>
                    <h2>{[campagne.code, campagne.goal].filter(Boolean).join(' - ') || " - "}</h2>
                </div>
            </header>

            <div className="cd-global">
                {/* Bloc Description */}
                <div className="cd-card">
                    <h3>Description</h3>
                    <div className="cd-card-content">
                        {renderCardRow("Nom", campagne.name || "-")}
                        {renderCardRow("Dates", (campagne.start_date ? moment(campagne.start_date).format('DD/MM/YYYY') : "") + " - "
                        + (campagne.end_date ? moment(campagne.end_date).format('DD/MM/YYYY') : ""))}
                        {renderCardRow("Fréquence", campagne.frequency || "-")}
                        {renderCardRow("Objectif", campagne.goal || "-")}
                        {renderCardRow("Principe / Argument", campagne.description || "-")}
                        {renderCardRow("Fin d'affectation", campagne.end_assignment_leads_date || "-")}
                    </div>
                </div>

                {/* Bloc Cible */}
                <div className="cd-card">
                    <h3>Cible</h3>
                    <div className="cd-card-content">
                        {renderCardRow("Cible global initiale", ((selectedOccurrence && selectedOccurrence.targets_count) || 0) + " contacts")}
                        {renderCardRow("Site(s)", campagneSites.map((site: Site) => site.name).join(", "))}
                        <div className="cd-alert-container">
                            <div className="cd-alert">
                                <p>Ma cible à répartir</p>
                                {isLoading ? (
                                    <div>
                                        <Loader/>
                                    </div>
                                ) : (
                                    <span>{selectedOccurrence.user_targets_count}</span>
                                )}
                            </div>
                            <div className="cd-alert">
                                <p>Sur une période de</p>
                                {isLoading ? (
                                    <div>
                                        <Loader/>
                                    </div>
                                ) : (
                                    <span>{(selectedOccurrence.business_days_count || 0) + " jours ouvrés"}</span>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="cd-summary">
                {campaignOccurrences.length > 1 ?
                    <div className={"cd-select-occurrence"}>
                        <label htmlFor="occurrence-select">Occurrence(s)</label>
                        <div>
                            <select id="occurrence-select" onChange={(e) => changeOccurrenceSelected(Number(e.target.value))} value={selectedOccurrence.id || ""}>
                                {campaignOccurrences.map((occurrence: Occurrence) => (
                                    <option key={occurrence.id} value={occurrence.id}>{moment(occurrence.date).format("DD/MM/YYYY")}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    : null
                }
                {campagne.target_type !== CampagneTargetType.ADV ?
                    <div className={"cd-select-occurrence"}>
                        <label htmlFor="seller-type-select">Type de vendeur</label>
                        <div>
                            <select
                                id="seller-type"
                                onChange={(e) => {setSelectedSellerType(e.currentTarget.value as SelectedSellerType)}}
                                value={selectedSellerType}
                            >
                                <option key="all" value="all">Tous</option>
                                <option key="VN" value="VN">VN</option>
                                <option key="VO" value="VO">VO</option>
                            </select>
                        </div>
                    </div>
                    : null
                }
                <div className={"cd-select-lead-max"}>
                    <label htmlFor="max-lead-select">Nombre maximum {campagne.type === "B" ? "de leads" : "d'actions"}</label>
                    <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                        <input
                            type="number"
                            style={{ width: "60px" }}
                            value={maxLeadNumber}
                            onChange={(e) => changeMaxLeadNumber(Number(e.target.value))}
                        />
                        <p style={{ margin: 0 }}>{campagne.type === "B" ? "leads" : "actions"} / jour</p>
                    </div>
                </div>
            </div>

            <div className="cd-select-site">
                <label htmlFor="site-select">Sites:</label>
                <select
                    id="site-select"
                    value={dealershipID || (sites.length > 0 ? sites[0].id : "")}
                    onChange={(e) => onChangeDealership(Number(e.target.value))}
                >
                    {sites.map((site: Site) => (
                        <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                </select>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px", alignItems: "center", gap: "10px" }}>
                <p style={{ margin: 0 }}>Voir les statistiques</p>
                <Switch checked={isStatisticsView} setChecked={setIsStatisticsView}/>
            </div>


            <table className="table table-striped cd-table">
                <thead>
                <tr>
                    <th>{sellerLabel}</th>
                    <th>Relations<br/>affectées</th>
                    <th>Contacts non assignés<br/>affectés</th>
                    {!isStatisticsView ? <th>Affectation</th> : null}
                    {!isStatisticsView ? <th>Solde<br/>des relations</th> : null}
                    {isStatisticsView ? <th>Leads traités</th> : null}
                    {isStatisticsView ? <th>Leads traités %</th> : null}
                </tr>
                </thead>
                {isLoading ? (
                    <tbody>
                    <tr>
                        <td colSpan={8} style={{ padding: 0 }}>
                            <div className="cd-loader">
                                <Loader/>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                ) : (
                    <tbody>
                    {
                        targetsDistributionFiltered.length !== 0 ? (
                            <>
                                {[ ...targetsDistributionFiltered ]
                                    .sort((a: TargetDistribution, b: TargetDistribution) => (a.followed_by.name || "").localeCompare(b.followed_by.name || ""))
                                    .map((targetDistribution: TargetDistribution) => {
                                    const percentageLeadsProcessed = targetDistribution.leads_targets_count === 0
                                        ? 0
                                        : Math.round((targetDistribution.processed_leads_targets_count / targetDistribution.leads_targets_count) * 100)
                                    let color = 'red'
                                    if (percentageLeadsProcessed >= 50) {
                                        color = 'orange'
                                    }
                                    if (percentageLeadsProcessed >= 75) {
                                        color = 'green'
                                    }
                                    return !targetDistribution.is_shared_pool && targetDistribution.followed_by.id !== 0 && (
                                        <tr key={targetDistribution.followed_by.id}>
                                            <td>{targetDistribution.followed_by.name}</td>
                                            {/* Hors vue statistiques on projette l'affectation en cours de saisie : la colonne
                                                reflète ce qui sera affecté après validation, pas seulement l'état serveur. */}
                                            <td>{targetDistribution.followed_with_assignment_count + (!isStatisticsView ? (targetDistribution.selected_targets || 0) : 0)} / {targetDistribution.targets_count}</td>
                                            {/* Contacts effectivement enregistrés en base comme assignés à ce commercial (assigned_targets_count)
                                                mais hors de son propre quota suivi (followed_and_assigned_targets_count) : dans ce flux, la seule
                                                source de ce type d'affectation est la répartition du solde des contacts non assignés. */}
                                            <td>{Math.max(0, (targetDistribution.assigned_targets_count || 0) - (targetDistribution.followed_and_assigned_targets_count || 0))}</td>
                                            {!isStatisticsView ? <td>
                                                {(targetDistribution.targets_count - targetDistribution.followed_with_assignment_count) > 0 ? (
                                                    <>
                                                        <input type="number"
                                                               value={targetDistribution.selected_targets}
                                                               style={{ width: "60px" }}
                                                               onChange={(e) => changeTargetDistribution(targetDistribution, Number(e.target.value))}
                                                               max={targetDistribution.targets_count - targetDistribution.followed_with_assignment_count}
                                                               min={0}
                                                        />
                                                        {redistributedCountByUser[targetDistribution.followed_by.id] > 0
                                                            ? <span style={{ marginLeft: "10px" }} title="Part du solde restant attribuée">
                                                                + {redistributedCountByUser[targetDistribution.followed_by.id]}
                                                            </span>
                                                            : null}
                                                        {/* L'attribut max de l'input n'empêche pas la saisie : on signale le dépassement. */}
                                                        {targetDistribution.selected_targets > (targetDistribution.targets_count - targetDistribution.followed_with_assignment_count)
                                                            ? <span
                                                                style={{ marginLeft: "10px", color: "#e08e0b", cursor: "help" }}
                                                                title={`Affectation supérieure au maximum disponible pour ce ${sellerLabel.toLowerCase()} (${targetDistribution.targets_count
                                                                - targetDistribution.followed_with_assignment_count})`}
                                                            >⚠</span>
                                                            : null}
                                                    </>
                                                ) : "—"}
                                            </td> : null}
                                            {!isStatisticsView ? <td>{targetDistribution.solde || 0}</td> : null}
                                            {isStatisticsView ? <td>{targetDistribution.processed_leads_targets_count} / {targetDistribution.leads_targets_count}</td> : null}
                                            {isStatisticsView ? <td style={{ color: color }}>{percentageLeadsProcessed}%</td> : null}
                                        </tr>
                                    )
                                })}
                                {targetsDistributionFiltered
                                    .filter((target: TargetDistribution) => !target.is_shared_pool && target.followed_by.id === 0)
                                    .map((targetDistribution: TargetDistribution, i: number) => (
                                        <tr key={`unassigned-${targetDistribution.followed_by.id}`}>
                                            <td>Contacts non assignés</td>
                                            <td>{targetDistribution.followed_with_assignment_count} / {targetDistribution.targets_count}</td>
                                            <td>—</td>
                                            {/* Rien à répartir : on masque le bouton. Il reste accessible tant qu'une
                                                répartition est en cours, sinon le solde tombe à 0 et on ne peut plus
                                                la modifier ni la réinitialiser. */}
                                            {!isStatisticsView ? <td>
                                                {(targetDistribution.solde > 0 || hasRedistribution) ? (
                                                    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                                            <span
                                                                data-for="attribuer-solde-tooltip"
                                                                data-tip={eligibleSellers.length === 0 ? `Aucun ${sellerLabel.toLowerCase()} n'est sélectionnable pour ce site.` : undefined}
                                                            >
                                                                <button
                                                                    className="btn btn-link cd-link-button"
                                                                    disabled={isRedistributing || eligibleSellers.length === 0}
                                                                    onClick={() => setIsSellerModalOpen(true)}
                                                                >
                                                                    Attribuer le solde restant
                                                                </button>
                                                            </span>
                                                            <ReactTooltip id="attribuer-solde-tooltip" place="top"/>
                                                        </div>
                                                        {isRedistributing
                                                                ? <span style={{ whiteSpace: "nowrap" }}>
                                                                    <i className="fa fa-refresh fa-spin fa-fw"/> Répartition en cours…
                                                                </span>
                                                                : null}
                                                        {hasRedistribution
                                                            ? <div style={{ fontSize: "12px", color: "#666" }}>
                                                                <span
                                                                    data-for="redistribution-summary-tooltip"
                                                                    data-tip="redistribution-summary"
                                                                    style={{ cursor: "help", textDecoration: "underline dotted" }}
                                                                >
                                                                    {redistributedTotal} {redistributedTotal > 1 ? "contacts répartis" : "contact réparti"} sur{" "}
                                                                    {filteredRedistributionEntries.length}{" "}
                                                                    {filteredRedistributionEntries.length > 1 ? sellerLabelPlural.toLowerCase() : sellerLabel.toLowerCase()}
                                                                </span>
                                                                <ReactTooltip
                                                                    id="redistribution-summary-tooltip"
                                                                    place="top"
                                                                    getContent={() => (
                                                                        <div>
                                                                            {filteredRedistributionEntries.map((td: RedistributionEntry) => (
                                                                                <div key={td.followed_by.id}>
                                                                                    {td.followed_by.name} ({(td.target_ids || []).length})
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                />
                                                                {" "}
                                                                <button
                                                                    className="btn btn-link btn-sm"
                                                                    style={{ padding: 0 }}
                                                                    onClick={resetRemainingDistribution}
                                                                >
                                                                    Réinitialiser
                                                                </button>
                                                            </div>
                                                            : null}
                                                    </div>
                                                ) : "—"}
                                            </td> : null}
                                            {!isStatisticsView ? <td>{targetDistribution.solde || 0}</td> : null}
                                            {isStatisticsView ? <td></td> : null}
                                            {isStatisticsView ? <td></td> : null}
                                        </tr>
                                    ))
                                }
                                <tr>
                                    <td className="bold">Total</td>
                                    <td>{`${(totalCounter.totalFollowedWithAssignment || 0) + (!isStatisticsView ? (totalCounter.totalSelected || 0) : 0)} / ${totalCounter.totalTargets || 0}`}</td>
                                    <td>—</td>
                                    {!isStatisticsView ? <td>{totalCounter.totalSelectedTargets || 0}</td> : null}
                                    {!isStatisticsView ? <td>{totalCounter.totalRemaining || 0}</td> : null}
                                    {isStatisticsView ? <td>{`${totalCounter.totalLeadsProcessed} / ${totalCounter.totalLeadsTargets}`}</td> : null}
                                    {isStatisticsView ? <td>{totalCounter.totalLeadsTargets === 0 ? 0 : Math.round(totalCounter.totalLeadsProcessed / totalCounter.totalLeadsTargets * 100)}%</td> : null}
                                </tr>
                            </>
                        ) : (
                            <tr>
                                <td colSpan={8} style={{ padding: 0 }}>
                                    <div className="cd-loader">
                                        <p>Aucun {sellerLabel.toLowerCase()} n'est rattaché à cette occurrence.</p>
                                    </div>
                                </td>
                            </tr>
                        )
                    }
                    </tbody>
                )}
            </table>
            <div style={{ display: "flex", justifyContent: "end" }}>
                <span data-for="submit-assignment-tooltip" data-tip={submitDisabledReason || undefined}>
                    <button
                        className="btn btn-default pull-right"
                        onClick={submitTargetsAssignment}
                        disabled={!!submitDisabledReason}
                    >
                        Attribuer
                    </button>
                </span>
                <ReactTooltip id="submit-assignment-tooltip" place="top"/>
            </div>

            <ModalRepartitionSolde
                isOpen={isSellerModalOpen}
                sellers={eligibleSellers}
                selectedSellerIds={selectedRedistributionSellerIds}
                previousAssignments={redistributedCountByUser}
                totalAvailable={unassignedSolde}
                sellerLabel={sellerLabel}
                sellerLabelPlural={sellerLabelPlural}
                onClose={() => setIsSellerModalOpen(false)}
                onValidate={distributeRemainingTargets}
            />
        </div>
    )
}
